/* ══════════════════════════════════════════════════════════════════════════
   HAVANI — PRODUCT

   Variant selection, gallery and add-to-cart. Everything reads the variant
   data Shopify serialises into the page, so nothing here knows a product
   name, a price or a variant id in advance.

   The sale state is not special-cased: a variant either has a
   compare_at_price above its price or it does not, and the price snippet is
   re-rendered from the server-side markup already in the DOM for every
   variant. That is what keeps the current all-on-sale catalogue and a future
   full-price product on the same code path.
   ══════════════════════════════════════════════════════════════════════════ */

(function () {
  'use strict';
  const $ = (s, r) => (r || document).querySelector(s);
  const $$ = (s, r) => Array.from((r || document).querySelectorAll(s));

  class HvVariants extends HTMLElement {
    connectedCallback() {
      this.section = this.closest('[data-section-id]');
      this.sectionId = this.section?.dataset.sectionId;
      this.url = this.dataset.url;
      try {
        this.variants = JSON.parse($('[data-variant-json]', this)?.textContent || '[]');
      } catch (e) {
        this.variants = [];
      }
      this.addEventListener('change', () => this.onChange());
      this.updateAvailability();
    }

    get selectedOptions() {
      return $$('fieldset', this).map((set) => {
        const checked = $('input:checked', set);
        return checked ? checked.value : null;
      });
    }

    findVariant(options) {
      return this.variants.find((v) =>
        (v.options || []).every((opt, i) => !options[i] || opt === options[i])
      );
    }

    /* grey out and strike through option values that cannot be reached from
       the current selection — an unavailable combination is a designed state,
       not a dead button */
    updateAvailability() {
      const selected = this.selectedOptions;
      $$('fieldset', this).forEach((set, index) => {
        $$('.swatch', set).forEach((swatch) => {
          const input = $('input', swatch);
          if (!input) return;
          const probe = selected.slice();
          probe[index] = input.value;
          const match = this.variants.find((v) =>
            (v.options || []).every((opt, i) => (i === index ? opt === input.value : opt === probe[i]))
          );
          const unavailable = !match || !match.available;
          swatch.classList.toggle('swatch--unavailable', unavailable && !swatch.classList.contains('swatch--soon'));
          swatch.setAttribute('data-available', String(!unavailable));
        });
      });
    }

    async onChange() {
      const variant = this.findVariant(this.selectedOptions);
      this.updateAvailability();

      const label = $('[data-option-selected]', this);
      const firstChecked = $('input:checked', this);
      if (label && firstChecked) label.textContent = firstChecked.value;

      if (!variant) {
        this.setUnavailable();
        return;
      }

      /* keep the URL shareable and the back button honest */
      if (this.url && window.history.replaceState) {
        window.history.replaceState({}, '', `${this.url}?variant=${variant.id}`);
      }

      const idInput = $('input[name="id"]', this.section);
      if (idInput) idInput.value = variant.id;

      $('hv-gallery', this.section)?.showVariantMedia(variant.featured_media_id);

      await this.renderFromServer(variant.id);
      document.dispatchEvent(new CustomEvent('hv:variant:change', { detail: { variant } }));
    }

    setUnavailable() {
      const button = $('[data-add-button]', this.section);
      if (button) {
        button.setAttribute('disabled', 'disabled');
        const text = $('[data-add-text]', button);
        if (text) text.textContent = window.hvStrings?.unavailable || 'Unavailable';
      }
      $('[data-buy-now]', this.section)?.setAttribute('disabled', 'disabled');
    }

    /* Re-render price, availability and the buy button from Liquid rather
       than rebuilding them in JavaScript, so there is exactly one source of
       truth for how money and stock are formatted. */
    async renderFromServer(variantId) {
      if (!this.url || !this.sectionId) return;
      try {
        const res = await fetch(`${this.url}?variant=${variantId}&section_id=${this.sectionId}`);
        const html = await res.text();
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        ['[data-price-block]', '[data-stock-block]', '[data-buy-block]', '[data-sku-block]'].forEach((sel) => {
          const fresh = parsed.querySelector(sel);
          const live = $(sel, this.section);
          if (fresh && live) live.innerHTML = fresh.innerHTML;
        });
      } catch (e) {
        /* leave the last good render in place */
      }
    }
  }
  if (!customElements.get('hv-variants')) customElements.define('hv-variants', HvVariants);

  /* Gallery. Handles one image, five images, or media added later — the
     thumbnail rail simply is not drawn when there is nothing to switch to. */
  class HvGallery extends HTMLElement {
    connectedCallback() {
      /* One AbortController per connection, the convention every other
         component here follows. The theme editor moves a section's node on
         reorder, which disconnects and reconnects the SAME element — without
         this, each move left another copy of every thumbnail listener. */
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();
      const signal = this.controller.signal;

      this.stage = $$('[data-media-item]', this);
      this.thumbs = $$('[data-media-thumb]', this);
      if (this.thumbs.length < 2) $('[data-media-thumbs]', this)?.setAttribute('hidden', 'hidden');
      this.thumbs.forEach((thumb, i) => {
        thumb.addEventListener('click', () => this.show(i), { signal });
        thumb.addEventListener('keydown', (e) => {
          if (e.key === 'ArrowRight') this.show(Math.min(i + 1, this.thumbs.length - 1), true);
          if (e.key === 'ArrowLeft') this.show(Math.max(i - 1, 0), true);
        }, { signal });
      });

      /* THE VIEWER HANDS THE GALLERY BACK. A shopper who opens the main image,
         arrows along to medium 4 and closes should be left looking at medium 4,
         not snapped back to medium 1. hv-viewer reports the index it ended on
         and this selects it — one media set, one active index, two surfaces. */
      /* PHONES: the stage is a native swipe strip (see havani-fixes.css). The
         active slide, the dots and video pausing follow the scroll position. */
      this.stageEl = $('.gallery__stage', this);
      this.dots = $$('.gallery__dots i', this);
      this.mq = window.matchMedia('(max-width: 700px)');
      let raf = 0;
      this.stageEl?.addEventListener('scroll', () => {
        if (!this.mq.matches || raf) return;
        raf = requestAnimationFrame(() => {
          raf = 0;
          const st = this.stageEl;
          const first = this.stage[0];
          if (!first) return;
          const step = first.getBoundingClientRect().width + (parseFloat(getComputedStyle(st).columnGap) || 0);
          const max = st.scrollWidth - st.clientWidth;
          let i = step > 0 ? Math.round(st.scrollLeft / step) : 0;
          if (st.scrollLeft >= max - 2) i = this.stage.length - 1;
          this.mark(Math.max(0, Math.min(this.stage.length - 1, i)));
        });
      }, { passive: true, signal });

      this.addEventListener('hv:viewer-close', (e) => {
        const i = e.detail && e.detail.index;
        if (typeof i === 'number' && i >= 0 && i < this.stage.length) this.show(i);
      }, { signal });
    }
    disconnectedCallback() {
      if (this.controller) { this.controller.abort(); this.controller = null; }
    }
    mark(index) {
      this.thumbs.forEach((t, i) => t.setAttribute('aria-current', String(i === index)));
      this.dots.forEach((d, i) => d.classList.toggle('is-on', i === index));
      this.stage.forEach((s, i) => {
        s.classList.toggle('is-active', i === index);
        const video = s.querySelector('video');
        if (video && i !== index) video.pause();
      });
    }
    show(index, focus) {
      this.mark(index);
      if (this.mq && this.mq.matches && this.stageEl && this.stage[index]) {
        this.stageEl.scrollTo({ left: this.stage[index].offsetLeft - this.stage[0].offsetLeft, behavior: 'smooth' });
      }
      if (focus) this.thumbs[index]?.focus();
    }
    showVariantMedia(mediaId) {
      if (!mediaId) return;
      const index = this.stage.findIndex((s) => s.dataset.mediaId === String(mediaId));
      if (index > -1) this.show(index);
    }
  }
  if (!customElements.get('hv-gallery')) customElements.define('hv-gallery', HvGallery);

  /* the product form: real add to cart, designed feedback, honest errors */
  class HvProductForm extends HTMLElement {
    connectedCallback() {
      this.form = $('form', this);
      if (!this.form) return;
      this.form.addEventListener('submit', (e) => this.submit(e));
    }
    async submit(e) {
      e.preventDefault();
      const buyNow = !!(e.submitter && e.submitter.hasAttribute('data-buy-now'));
      /* ⚠️ SAME DOUBLE-SUBMIT AS QUICK ADD (§4.20). `is-loading` is a class,
         not a guard: a second press before the fetch resolved sent a second
         `/cart/add.js` and put two in the bag. `disabled` is what stops it —
         and `wasDisabled` is remembered because a sold-out variant renders
         this button already disabled, which must never be re-enabled here. */
      if (this.busy) return;
      this.busy = true;
      const button = buyNow ? e.submitter : $('[data-add-button]', this.form);
      const wasDisabled = button ? button.disabled : false;
      const errorBox = $('[data-add-error]', this);
      if (errorBox) errorBox.hidden = true;
      if (button) {
        button.disabled = true;
        button.classList.add('is-loading');
      }
      try {
        await window.HV.cart.add(new FormData(this.form));
        if (buyNow) {
          /* straight to checkout — Shop Pay, Apple Pay etc. are offered there */
          window.location.href = (window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/') + 'checkout';
          return;
        }
        window.HV.toast(window.hvStrings?.added || 'Added to bag');
        const drawer = $('#hv-cart-drawer');
        if (drawer && this.dataset.openDrawer !== 'false') window.HV.overlays.open(drawer);
      } catch (err) {
        if (errorBox) {
          errorBox.hidden = false;
          $('[data-add-error-text]', errorBox).textContent = err.message;
        } else {
          window.HV.toast(err.message, true);
        }
      } finally {
        this.busy = false;
        if (button) {
          button.disabled = wasDisabled;
          button.classList.remove('is-loading');
        }
      }
    }
  }
  if (!customElements.get('hv-product-form')) customElements.define('hv-product-form', HvProductForm);

  /* sticky mobile buy bar — mirrors the main button rather than duplicating it */
  class HvStickyBuy extends HTMLElement {
    connectedCallback() {
      this.target = $(this.dataset.watch || '[data-buy-block]');
      if (!this.target) return;
      this.observer = new IntersectionObserver(
        /* Only once the buy button has scrolled UP out of view. It used to show
           whenever the button was off screen — including at page load on a
           phone, when the button is simply further down — and it sat on top
           of the product name. */
        ([entry]) => this.classList.toggle('is-visible', !entry.isIntersecting && entry.boundingClientRect.top < 0),
        { rootMargin: '-120px 0px 0px 0px' }
      );
      this.observer.observe(this.target);
      $('button', this)?.addEventListener('click', () => {
        $('[data-add-button]')?.click();
      });
    }
    disconnectedCallback() { if (this.observer) this.observer.disconnect(); }
  }
  if (!customElements.get('hv-sticky-buy')) customElements.define('hv-sticky-buy', HvStickyBuy);

  /* SHARE — the phone's own share sheet where there is one, copy-link otherwise. */
  if (navigator.share) {
    $$('[data-share-native][data-share-label]').forEach((b) => {
      const t = b.querySelector('span');
      if (t) t.textContent = b.dataset.shareLabel;
    });
  }
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-share-native]');
    if (!btn) return;
    e.preventDefault();
    const url = btn.dataset.shareUrl || window.location.href;
    const title = btn.dataset.shareTitle || document.title;
    if (navigator.share) {
      try { await navigator.share({ title, url }); } catch (err) { /* dismissed */ }
      return;
    }
    try {
      await navigator.clipboard.writeText(url);
      if (window.HV && window.HV.toast) window.HV.toast(btn.dataset.copied || 'Link copied');
    } catch (err) {
      window.prompt('Copy this link', url);
    }
  });
})();
